import { Outlet, useLocation, useNavigate } from 'react-router-dom'
import { useAppSelector } from 'store'
import { hasUrlParams } from 'utils/comparison-initializer'
import { RoutePaths } from 'types/enums'
import type { IUser } from 'types/interfaces'
import auth from 'services/auth'

/** URL has autoload=1 and cover_ids or policy_ids (does not require userAccount) */
function hasAutoloadParamsInUrl(search: string): boolean {
  const params = new URLSearchParams(search)
  return params.get('autoload') === '1' && (params.has('cover_ids') || params.has('policy_ids'))
}

/**
 * Determines if we should redirect to comparison for auto-load.
 * Uses URL params only so redirect works when opening from new tab (userAccount
 * may not be loaded yet). User feature check happens in Comparison init.
 */
export function shouldRedirectToComparison(
  pathname: string,
  search: string,
  userAccount: IUser | null | undefined,
): boolean {
  const hasParamsInUrl = hasAutoloadParamsInUrl(search)
  const hasUserFeature = hasUrlParams(new URLSearchParams(search), userAccount)
  const isOnComparison = pathname === RoutePaths.COMPARISON
  const isOnLoginWithoutAgreement = pathname === RoutePaths.LOGIN && !auth.disclaimerAgreed

  return (hasParamsInUrl || hasUserFeature) && !isOnComparison && !isOnLoginWithoutAgreement
}

/**
 * Route-level guard: when autoload params are detected and user has agreed
 * to disclaimer (if on login), redirect to comparison so initialization runs.
 * Redirects on URL params alone so new-tab opens reach comparison immediately.
 */
function AutoloadRedirectGuard() {
  const location = useLocation()
  const navigate = useNavigate()
  const userAccount = useAppSelector((state) => state.auth.account)

  if (shouldRedirectToComparison(location.pathname, location.search, userAccount)) {
    navigate({ pathname: RoutePaths.COMPARISON, search: location.search }, { replace: true })
  }

  return <Outlet />
}

export default AutoloadRedirectGuard
